import { PairwiseDifferenceKindEnum } from "@api/rest/types-and-hooks";

/** Backend enum mirrors + finite UI states for the Model Testing tab.
 *  Values match api-django's EvalModelComparison / EvalModelComparisonRun /
 *  EvalModelComparisonPick choices — keep in sync when the backend adds one. */

export const Arm = {
  Baseline: "baseline",
  Candidate: "candidate",
} as const;
export type ArmValue = (typeof Arm)[keyof typeof Arm];

export const Winner = {
  Baseline: "baseline",
  Candidate: "candidate",
  Tie: "tie",
} as const;
export type WinnerValue = (typeof Winner)[keyof typeof Winner];

export const Verdict = {
  Undecided: "undecided",
  SafeToSwitch: "safe_to_switch",
  NotSafe: "not_safe",
} as const;
export type VerdictValue = (typeof Verdict)[keyof typeof Verdict];

export const VERDICT_LABELS: Record<VerdictValue, string> = {
  [Verdict.Undecided]: "Undecided",
  [Verdict.SafeToSwitch]: "Safe to switch",
  [Verdict.NotSafe]: "Not safe",
};

/** PromptTestRun.status values (mirrors the test bed's run statuses). */
export const RunStatus = {
  Running: "running",
  Complete: "complete",
  Failed: "failed",
} as const;
export type RunStatusValue = (typeof RunStatus)[keyof typeof RunStatus];

/** How the two arms' outputs relate, once both are complete. */
export const OutputMatch = {
  Identical: "identical",
  Different: "different",
} as const;
export type OutputMatchValue = (typeof OutputMatch)[keyof typeof OutputMatch];

// Score chip tones: red when the judge failed anything, amber while
// anything is unsure or unjudged, green when everything passed.
export const SCORE_TONES = {
  pass: "bg-green-100 text-green-700",
  review: "bg-amber-100 text-amber-700",
  fail: "bg-red-100 text-red-600",
} as const;

export interface ScoreCounts {
  worse: number;
  unsure: number;
  not_judged: number;
  disqualified: number;
}

export const scoreTone = (counts: ScoreCounts): string => {
  if (counts.worse > 0 || counts.disqualified > 0) return SCORE_TONES.fail;
  if (counts.unsure > 0 || counts.not_judged > 0) return SCORE_TONES.review;
  return SCORE_TONES.pass;
};

/** Sum of several summaries' counts (a model's cells, say). */
export const sumScoreCounts = (summaries: ScoreCounts[]): ScoreCounts =>
  summaries.reduce(
    (acc, s) => ({
      worse: acc.worse + s.worse,
      unsure: acc.unsure + s.unsure,
      not_judged: acc.not_judged + s.not_judged,
      disqualified: acc.disqualified + s.disqualified,
    }),
    { worse: 0, unsure: 0, not_judged: 0, disqualified: 0 },
  );

/** Human-readable model name from a provider id:
 *  "gemini-3.5-flash" → "Gemini 3.5 Flash",
 *  "us.anthropic.claude-sonnet-4-6" → "Claude Sonnet 4.6".
 *  Vendor prefixes drop, dash-separated version digits join with dots,
 *  date-stamp suffixes (8+ digits) drop. Purely cosmetic — API payloads
 *  always carry the raw id. */
export const modelLabel = (id: string): string => {
  // Strip letters-only dotted vendor prefixes ("us.anthropic.") without
  // touching version dots ("gemini-3.5-flash").
  const base = id.replace(/^(?:[a-z]+\.)+/i, "");
  const parts = base.split("-").filter((p) => p.length > 0 && !/^\d{8,}$/.test(p));
  const words: string[] = [];
  let i = 0;
  while (i < parts.length) {
    const part = parts[i];
    if (/^\d+(\.\d+)?$/.test(part)) {
      const nums = [part];
      while (i + 1 < parts.length && /^\d+$/.test(parts[i + 1])) {
        nums.push(parts[i + 1]);
        i++;
      }
      words.push(nums.join("."));
    } else {
      words.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
    i++;
  }
  return words.join(" ") || id;
};

/** Section page a slot lives on. DIAGNOSIS_EVALUATION conclusions-page
 *  slots run LAST — their prompts consume the other sections' outputs,
 *  which the server injects once the same arm's sections complete. */
export const PageLocation = {
  Conclusions: "conclusions",
} as const;

/** Landing-page views: multi-model tests span every org's templates, comparisons
 *  filter by org — different scopes, so separate tabs. */
export const ModelTestingView = {
  Multi: "multi",
  Comparison: "comparison",
} as const;
export type ModelTestingViewValue = (typeof ModelTestingView)[keyof typeof ModelTestingView];

/** One arm's client-side state. An unstarted slot is "pending", distinct
 *  from "running" — the fix is to start it, not wait. */
export const ArmState = {
  Pending: "pending",
  Running: "running",
  Complete: "complete",
  Failed: "failed",
} as const;
export type ArmStateValue = (typeof ArmState)[keyof typeof ArmState];

// Side-by-side judge verdict on a slot (candidate vs current output).
export const PairwiseVerdict = {
  Same: "same",
  Better: "better",
  Worse: "worse",
  Unsure: "unsure",
} as const;
export type PairwiseVerdictValue = (typeof PairwiseVerdict)[keyof typeof PairwiseVerdict];

// Judge lifecycle on a slot; pending/running are still in flight.
export const PairwiseStatus = {
  Pending: "pending",
  Running: "running",
  Complete: "complete",
  Failed: "failed",
} as const;

export const PAIRWISE_VERDICT_LABELS: Record<PairwiseVerdictValue, string> = {
  [PairwiseVerdict.Same]: "Same",
  [PairwiseVerdict.Better]: "Better",
  [PairwiseVerdict.Worse]: "Worse",
  [PairwiseVerdict.Unsure]: "Not sure",
};

export const PAIRWISE_VERDICT_CHIP_LABELS: Record<PairwiseVerdictValue, string> = {
  [PairwiseVerdict.Same]: "Same as current",
  [PairwiseVerdict.Better]: "Better than current",
  [PairwiseVerdict.Worse]: "Worse than current",
  [PairwiseVerdict.Unsure]: "Not sure",
};

// No semantic Mosaic token for verdict tones; same palette as the score tones above.
export const PAIRWISE_VERDICT_TONES: Record<PairwiseVerdictValue, string> = {
  [PairwiseVerdict.Same]: "bg-green-100 text-green-700",
  [PairwiseVerdict.Better]: "bg-green-100 text-green-700",
  [PairwiseVerdict.Worse]: "bg-red-100 text-red-700",
  [PairwiseVerdict.Unsure]: "bg-amber-100 text-amber-700",
};

export const PAIRWISE_VERDICT_RULES: Record<PairwiseVerdictValue, string> = {
  [PairwiseVerdict.Same]: "border-green-400",
  [PairwiseVerdict.Better]: "border-green-400",
  [PairwiseVerdict.Worse]: "border-red-400",
  [PairwiseVerdict.Unsure]: "border-amber-400",
};

// What a judged difference does to the candidate, and whether it matters.
export const DifferenceEffect = { Worse: "worse", Better: "better", Neutral: "neutral" } as const;
export const DifferenceSeverity = { Material: "material", Minor: "minor" } as const;

// Impact chips on difference cards; neutral differences get none. No Mosaic
// token for these tones; same palette as the verdict chips.
export const DIFFERENCE_IMPACTS = {
  materialLoss: { label: "Material loss", tone: "bg-red-100 text-red-700" },
  minorLoss: { label: "Minor loss", tone: "bg-amber-100 text-amber-700" },
  materialGain: { label: "Material gain", tone: "bg-green-100 text-green-700" },
  minorGain: { label: "Minor gain", tone: "border border-border-subtle bg-bg-default text-text-secondary" },
} as const;

export const PAIRWISE_DIFFERENCE_LABELS: Record<PairwiseDifferenceKindEnum, string> = {
  [PairwiseDifferenceKindEnum.format]: "Breaks the format",
  [PairwiseDifferenceKindEnum.instruction]: "Ignores an instruction",
  [PairwiseDifferenceKindEnum.missing]: "Missing",
  [PairwiseDifferenceKindEnum.changed]: "Changed",
  [PairwiseDifferenceKindEnum.added]: "Added",
  [PairwiseDifferenceKindEnum.contradicts_source]: "Contradicts the source",
  [PairwiseDifferenceKindEnum.wording]: "Wording only",
};
